import { z } from "zod";
export const ConversationRuleSchema = z.enum([
    "READ_ONLY",
    "INTERNAL_REVIEW",
    "SCOPED_TEAM_REVIEW",
    "OWNER_REVIEW",
    "EXTERNAL_REVIEW"
]);
export const ConversationRuleUpdateSchema = z.object({
    expectedVersion: z.number().int().min(0),
    rule: ConversationRuleSchema,
    clientId: z.uuid().nullable().default(null)
}).strict().superRefine((value, context) => {
    if (value.rule === "SCOPED_TEAM_REVIEW" && !value.clientId) {
        context.addIssue({ code: "custom", path: ["clientId"], message: "Choose the client for that internal team conversation." });
    }
    if (value.rule !== "SCOPED_TEAM_REVIEW" && value.clientId) {
        context.addIssue({ code: "custom", path: ["clientId"], message: "Only a scoped team rule may select a client." });
    }
});
/**
 * WHY [BOL-AUDIENCE-SCOPE-001]: conversation text is untrusted evidence, not
 * authorization. Work rows must be fenced before an agent, rule, or model can
 * retrieve them. A prompt saying "do not leak client data" is not a boundary.
 *
 * INTERNAL_OPS and PRIVATE_OWNER may intentionally see the workspace when no
 * narrower client/project fence is configured. TEAM must name at least one
 * fence. CLIENT stays fail-closed until a publication-aware resolver exists;
 * internal board rows are never silently treated as client-safe output.
 * EXTERNAL_CONTACT is narrower still: only the separately human-approved
 * exact-message executor may publish to it, never general Work retrieval.
 */
export function evaluateOperationalWorkScope(scope) {
    if (scope.audience === "OBSERVE_ONLY")
        return { allowed: false, reason: "CONVERSATION_OBSERVE_ONLY" };
    if (scope.audience === "CLIENT")
        return { allowed: false, reason: "CLIENT_OUTPUT_NOT_CONFIGURED" };
    if (scope.audience === "EXTERNAL_CONTACT") {
        return { allowed: false, reason: "EXTERNAL_CONTACT_OUTPUT_NOT_CONFIGURED" };
    }
    if (!scope.allowedFactSensitivities.includes("INTERNAL")) {
        return { allowed: false, reason: "INTERNAL_FACTS_NOT_ALLOWED" };
    }
    if (scope.audience === "TEAM" && !scope.clientId && !scope.projectId) {
        return { allowed: false, reason: "TEAM_SCOPE_NOT_CONFIGURED" };
    }
    return { allowed: true };
}
/**
 * Keep the same object-level fence at candidate retrieval, proposal creation,
 * and receipt execution. Any one of those layers may catch a policy race or a
 * future caller that skipped an earlier check.
 */
export function conversationScopeAllowsWork(scope, work) {
    if (!evaluateOperationalWorkScope(scope).allowed)
        return false;
    if (scope.clientId && work.clientId !== scope.clientId)
        return false;
    if (scope.projectId && work.projectId !== scope.projectId)
        return false;
    return true;
}
//# sourceMappingURL=conversation-scope.js.map